import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Icon,
  Modal,
  Note,
  Table,
  Tag,
  Tooltip,
  type TableColumn,
} from '@repo/design-system';
import { accent, ink, radius, rgba, shadow, space, surface, typeRole } from '@repo/design-tokens';
import type {
  DroppedDraftSummary,
  FloorPlannerLayout,
  TwinLaunchSummary,
} from '@repo/frontend-types';
import { IconDisc, visuallyHidden } from '@repo/ui';

import type { DraftChoiceError } from './useDraftChoice';
import type { LaunchHistoryState } from './useLaunchHistory';
import { startedBeforeProduction } from './draftChoice';
import DraftChoiceErrorBanner from './DraftChoiceErrorBanner';
import { whenText } from './draftChoiceText';
import {
  HISTORY_FOOTNOTE,
  droppedRows,
  historyRows,
  planSummaryText,
  type HistoryRow,
} from './launchHistory';
import { DISCARD_HINT, isCurrentUser, type ChangePill } from './versionsText';

/** A type role as the `font` shorthand; shared with the Load confirmation. */
export const font = (t: {
  fontWeight: number | string;
  fontSize: string;
  lineHeight: string;
  fontFamily: string;
}) => `${t.fontWeight} ${t.fontSize} / ${t.lineHeight} ${t.fontFamily}`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};

  p {
    margin: 0;
  }
`;

/*
 * The draft header, as frame 08 draws it: a warm glass card — white into butter at 135°, a white
 * hairline, the frost glow — with the shared `IconDisc`'s `draft` tone carrying the pen. App-local
 * (one host); built from the tokens the chip's own *Updating live twin* tint and `Tag`'s butter wash
 * use. Owed Rob's ratification (the spec's Human Action 1).
 */
const DraftCard = styled.section`
  /* The offset parent the Discard Draft tip is anchored in. */
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  padding: ${space[4]};
  border-radius: ${radius.lg};
  background: linear-gradient(135deg, ${rgba(surface[0], 0.9)}, ${rgba(accent.butter, 0.18)});
  border: 1.5px solid ${rgba(surface[0], 0.95)};
  box-shadow: ${shadow.glassHover};

  .draft-top {
    display: flex;
    align-items: center;
    gap: ${space[3]};
  }

  .draft-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .draft-title {
    font: ${font(typeRole.subtitle2)};
    color: ${ink[1]};
    margin: 0;
  }

  .draft-sub {
    font: ${font(typeRole.caption1)};
    color: ${ink[3]};
  }

  .draft-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: ${space[2]};
  }

  /* The pills sit under the title, past the disc (frame 08's 46px indent: the disc and its gap). */
  .draft-changes {
    margin-left: 46px;
  }

  /* The stale-draft warning takes the pills' indent (not drawn in frame 08: Rob to confirm). */
  .draft-stale {
    margin-left: 46px;
  }

  .draft-launch-reason {
    margin: 0 0 0 46px;
    font: ${font(typeRole.caption1)};
    color: ${ink[3]};
  }

  .draft-changes-line {
    margin-left: 46px;
    display: flex;
    align-items: center;
    gap: ${space[2]};
    font: ${font(typeRole.caption1)};
    color: ${ink[3]};
  }
`;

/** The change pills: the step name in the quieter ink, then what changed there (frame 08). */
const PillList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: ${space[2]};

  .pill-step {
    color: ${ink[3]};
    margin-right: ${space[1]};
  }
`;

const Footnote = styled.p`
  font: ${font(typeRole.caption1)};
  color: ${ink[3]};
`;

const Sub = styled.span`
  color: ${ink[3]};
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  font: ${font(typeRole.body1)};
  color: ${ink[2]};
`;

/** What the draft changes against production, as Versions knows it (R4). */
export type ChangesState =
  | { state: 'loading' }
  /** `total`: every changed entry, counted once — the Load confirmation's count (load-launch, R3.1). */
  | { state: 'ready'; pills: ChangePill[]; total: number }
  | { state: 'failed' };

/** The dialog's own buttons, so Cancel on a confirmation can hand focus back to the one pressed. */
export type VersionsFocus = 'discard' | 'restore' | { load: string };

export interface VersionsModalProps {
  open: boolean;
  twinName: string;
  onClose: () => void;
  returnFocusTo?: { readonly current: HTMLElement | null };
  /** Which button to focus when the dialog opens — the one a cancelled confirmation came from. */
  focusOn?: VersionsFocus | null;
  read: LaunchHistoryState;
  /** The draft header (R3). */
  title: string;
  subline: string;
  /** *Discard Draft* is offered: production exists and the draft holds unlaunched work (R3.3). */
  canDiscard: boolean;
  /**
   * The draft as the wizard holds it now, and production's launch, for the warning that production
   * was launched again since the draft was started (`remove-draft-choice-modal`, R4). Null when
   * not known; the warning then stays quiet.
   */
  draftLayout: FloorPlannerLayout | null;
  production: Pick<TwinLaunchSummary, 'launchUuid'> | null;
  /** *Go to {launchLabel}* is offered: the draft is not launched (R3.4). */
  showGoToLaunch: boolean;
  launchLabel: string;
  /** Why the Launch step cannot be opened now, or null when it can. */
  launchDisabledReason: string | null;
  /** The by-step changes, shown only where Discard is (R4.4); null to show none. */
  changes: ChangesState | null;
  dropped: DroppedDraftSummary | null;
  droppedReadFailed: boolean;
  currentEmail: string | null | undefined;
  /** No swap may start, and why (a swap running, a reload owed, activation, a launch running). */
  lockReason: string | null;
  /** Which swap is running, so its button shows busy. */
  busy: null | 'production' | 'restore' | 'load';
  /** A Restore that changed nothing comes back here, with why. */
  restoreError: DraftChoiceError | null;
  onDiscard: () => void;
  onGoToLaunch: () => void;
  onLoad: (launch: TwinLaunchSummary) => void;
  onRestore: () => void;
  onRetryHistory: () => void;
  onRetryChanges: () => void;
  onReload: () => void;
}

const LAUNCH_DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
/** A launch or drop as a date — *3 Oct 2026* — as frame 08's table writes it. */
export const dateText = (iso: string | null): string => {
  if (!iso) return '';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '' : LAUNCH_DATE.format(at);
};

// Dense, as the BI tags are: a default-size tag is taller than the 32px row, which overflowed the
// table's body by 2px and drew a scroll thumb on a three-row table (review, 2026-10-16).
const versionCell = (row: HistoryRow): React.ReactNode => {
  switch (row.kind) {
    case 'dropped':
      return (
        <Tag surface="on-solid" size="dense" tone="neutral">
          Dropped draft
        </Tag>
      );
    case 'dropped-unread':
      return 'Dropped draft';
    case 'launch':
      if (row.status === 'in-progress')
        return (
          <Tag surface="on-solid" size="dense" tone="info">
            In progress
          </Tag>
        );
      if (row.status === 'stopped')
        return (
          <Tag surface="on-solid" size="dense" tone="warn">
            Stopped before it finished
          </Tag>
        );
      if (row.production)
        return (
          <Tag surface="on-solid" size="dense" tone="mint">
            {row.version ? `${row.version} · Production` : 'Production'}
          </Tag>
        );
      return row.version ?? 'Launch';
  }
};

const launchedCell = (row: HistoryRow): React.ReactNode => {
  switch (row.kind) {
    case 'dropped':
      return dateText(row.dropped.droppedAt);
    case 'dropped-unread':
      return "We couldn't check for a dropped draft. Reload to check again.";
    case 'launch':
      return row.status === 'completed'
        ? dateText(row.launch.completedAt)
        : `Started ${whenText(row.launch.launchedAt)}`;
  }
};

/**
 * *Versions of {twin}* (`twin-details-page-versions`, frame 08): the draft and what it changes, the
 * way back to production, the numbered launches and the dropped draft, from any step. Replaces the
 * Twin details history panel.
 *
 * Presentational: every rule is in `launchHistory.ts` and `versionsText.ts`, every write in
 * `useDraftChoice`. One dialog at a time — Discard, Load and Restore each close this and open their
 * own, and a cancelled confirmation reopens it with focus on the button that was pressed.
 */
const VersionsModal: React.FC<VersionsModalProps> = ({
  open,
  twinName,
  onClose,
  returnFocusTo,
  focusOn = null,
  read,
  title,
  subline,
  canDiscard,
  draftLayout,
  production,
  showGoToLaunch,
  launchLabel,
  launchDisabledReason,
  changes,
  dropped,
  droppedReadFailed,
  currentEmail,
  lockReason,
  busy,
  restoreError,
  onDiscard,
  onGoToLaunch,
  onLoad,
  onRestore,
  onRetryHistory,
  onRetryChanges,
  onReload,
}) => {
  const discardRef = useRef<HTMLButtonElement | null>(null);
  /** Where the Discard Draft tip points while it shows: the button's bottom centre (R3). */
  const [discardTip, setDiscardTip] = useState<{ x: number; y: number } | null>(null);
  /** What is holding the tip open: it hides only when neither hover nor focus remains. */
  const discardHold = useRef({ hover: false, focus: false });
  const discardTipFor = (via: 'hover' | 'focus', on: boolean, el: HTMLElement) => {
    discardHold.current[via] = on;
    if (on) {
      // Offsets against the draft card, the button's nearest positioned ancestor.
      setDiscardTip({ x: el.offsetLeft + el.offsetWidth / 2, y: el.offsetTop + el.offsetHeight });
    } else if (!discardHold.current.hover && !discardHold.current.focus) {
      setDiscardTip(null);
    }
  };
  // Escape dismisses a showing tip first, wherever focus is (WCAG 1.4.13): caught on `window` in
  // the capture phase, ahead of the Modal's own `document` listener, which then never sees it.
  // With no tip showing, Escape reaches the dialog and closes it, as before.
  const tipShowing = discardTip !== null;
  useEffect(() => {
    if (!tipShowing) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setDiscardTip(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [tipShowing]);
  const restoreRef = useRef<HTMLButtonElement | null>(null);
  const loadRefs = useRef(new Map<string, HTMLButtonElement>());
  /** A Try again is running: its button stays mounted, busy, until the read settles. */
  const [historyRetrying, setHistoryRetrying] = useState(false);
  const [changesRetrying, setChangesRetrying] = useState(false);
  useEffect(() => {
    if (read.state !== 'loading') setHistoryRetrying(false);
  }, [read.state]);
  useEffect(() => {
    if (changes?.state !== 'loading') setChangesRetrying(false);
  }, [changes?.state]);

  // Hand focus back to the button a cancelled confirmation came from, after the dialog's own
  // opening focus has run (it focuses on mount, so this waits a frame).
  useEffect(() => {
    if (!open || !focusOn) return undefined;
    const frame = requestAnimationFrame(() => {
      const target =
        focusOn === 'discard'
          ? discardRef.current
          : focusOn === 'restore'
            ? restoreRef.current
            : (loadRefs.current.get(focusOn.load) ?? null);
      target?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, focusOn]);

  const locked = lockReason !== null;
  /**
   * Production was launched again since this draft was started (R4) — the warning the opening
   * question used to give, shown where Discard Draft is offered and read from the layout as held now.
   */
  const staleDraft =
    canDiscard &&
    draftLayout !== null &&
    production !== null &&
    startedBeforeProduction(draftLayout, production);
  const rows =
    read.state === 'ready'
      ? historyRows({ history: read.history, dropped, droppedReadFailed })
      : read.state === 'failed'
        ? // The dropped draft does not depend on the history read: its Restore must stay reachable.
          droppedRows({ dropped, droppedReadFailed })
        : [];
  const noLaunches = read.state === 'ready' && read.history.launches.length === 0;

  const byCell = (row: HistoryRow): React.ReactNode => {
    switch (row.kind) {
      case 'dropped':
        // Unknown stays blank — never attributed to the person reading.
        if (!row.dropped.droppedByEmail) return '';
        return isCurrentUser(row.dropped.droppedByEmail, currentEmail)
          ? 'You'
          : row.dropped.droppedByEmail;
      case 'dropped-unread':
        return '';
      case 'launch': {
        const by = row.launch.launchedByEmail;
        if (!by) return '';
        return isCurrentUser(by, currentEmail) ? 'You' : <span title={by}>{by}</span>;
      }
    }
  };

  const summaryCell = (row: HistoryRow): React.ReactNode => {
    switch (row.kind) {
      case 'dropped':
        return <Sub>Kept until the next launch</Sub>;
      case 'dropped-unread':
        return '';
      case 'launch': {
        const floors = planSummaryText(row.launch);
        return (
          <>
            {floors}
            {floors && row.bi ? ' · ' : null}
            {row.bi && (
              <Tag surface="on-solid" tone={row.bi.tone} size="dense">
                {row.bi.label}
              </Tag>
            )}
          </>
        );
      }
    }
  };

  const actionCell = (row: HistoryRow): React.ReactNode => {
    if (row.kind === 'dropped') {
      return (
        <Button
          ref={restoreRef}
          type="button"
          variant="secondary"
          size="xs"
          aria-label="Restore the dropped draft"
          aria-describedby={locked ? 'versions-lock-reason' : undefined}
          onClick={onRestore}
          disabled={locked && busy !== 'restore'}
          loading={busy === 'restore'}
        >
          Restore
        </Button>
      );
    }
    if (row.kind !== 'launch' || !row.canLoad) return null;
    // The visible label first, so a speech user can say what they see (WCAG label in name).
    const name = row.version
      ? `Load as Draft: ${row.version}`
      : `Load as Draft: the launch of ${whenText(row.launch.completedAt)}`;
    return (
      <Button
        ref={el => {
          if (el) loadRefs.current.set(row.launch.launchUuid, el);
          else loadRefs.current.delete(row.launch.launchUuid);
        }}
        type="button"
        variant="secondary"
        size="xs"
        aria-label={name}
        aria-describedby={locked ? 'versions-lock-reason' : undefined}
        onClick={() => onLoad(row.launch)}
        disabled={locked}
      >
        Load as Draft
      </Button>
    );
  };

  const columns: TableColumn<HistoryRow>[] = [
    { key: 'version', header: 'Version', render: versionCell, width: '130px', resizable: false },
    { key: 'launched', header: 'Launched', render: launchedCell, width: '120px', resizable: false },
    { key: 'by', header: 'By', render: byCell, width: '170px', resizable: false },
    {
      key: 'summary',
      header: 'Configuration and BI',
      render: summaryCell,
      width: '1fr',
      resizable: false,
    },
    {
      key: 'action',
      header: 'Action',
      render: actionCell,
      width: '140px',
      align: 'right',
      resizable: false,
    },
  ];

  /*
   * The changes line is ONE live region while there is anything to say in it — loading, failed, or a
   * retry running — so a repeat failure is a change of text inside an existing region (which a screen
   * reader announces), never a region inserted already filled (which it does not). Try again stays
   * mounted, busy, while its retry runs, so focus is not dropped to the page (review, 2026-10-16).
   */
  const changesLine = (() => {
    if (!changes || changes.state === 'ready') return null;
    const failed = changes.state === 'failed';
    const retrying = changesRetrying && changes.state === 'loading';
    return (
      <div className="draft-changes-line" role="status">
        {failed
          ? "We couldn't work out what your draft changes."
          : 'Working out what your draft changes…'}
        {(failed || retrying) && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            aria-label="Try working out the changes again"
            loading={retrying}
            onClick={() => {
              setChangesRetrying(true);
              onRetryChanges();
            }}
          >
            Try again
          </Button>
        )}
      </div>
    );
  })();
  const changesBlock =
    changes?.state === 'ready' ? (
      changes.pills.length === 0 ? (
        <div className="draft-changes-line">
          No changes to the plan&apos;s floors, equipment, cameras, observers or measures
        </div>
      ) : (
        <ChangePills className="draft-changes" pills={changes.pills} />
      )
    ) : (
      changesLine
    );
  const historyRetry = historyRetrying && read.state === 'loading';

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={busy === null}
      title={`Versions of ${twinName}`}
      width="4xl"
      icon={<Icon name="clock-rotate-left" variant="solid" size="xs" />}
      closeLabel="Close versions"
      returnFocusTo={returnFocusTo}
    >
      <Body>
        {restoreError && <DraftChoiceErrorBanner error={restoreError} onReload={onReload} />}
        <DraftCard aria-label="Your draft">
          <div className="draft-top">
            <IconDisc tone="draft" size={34}>
              <Icon name="pen" variant="solid" size="xs" />
            </IconDisc>
            <div className="draft-text">
              <h3 className="draft-title">{title}</h3>
              <span className="draft-sub">{subline}</span>
            </div>
            <div className="draft-actions">
              {canDiscard && (
                <Button
                  ref={discardRef}
                  type="button"
                  variant="text"
                  tone="destructive"
                  size="inline"
                  iconLeft={<Icon name="rotate-left" variant="solid" size="xs" />}
                  onClick={onDiscard}
                  disabled={locked}
                  aria-describedby={
                    locked ? 'versions-discard-hint versions-lock-reason' : 'versions-discard-hint'
                  }
                  onMouseEnter={e => discardTipFor('hover', true, e.currentTarget)}
                  onMouseLeave={e => discardTipFor('hover', false, e.currentTarget)}
                  onFocus={e => discardTipFor('focus', true, e.currentTarget)}
                  onBlur={e => discardTipFor('focus', false, e.currentTarget)}
                >
                  Discard Draft
                </Button>
              )}
              {canDiscard && (
                // The description, always present — the tip is visual and shows only on demand.
                <span id="versions-discard-hint" style={visuallyHidden}>
                  {DISCARD_HINT}
                </span>
              )}
              {showGoToLaunch && (
                <>
                  <Button
                    type="button"
                    variant="primary"
                    size="inline"
                    iconLeft={<Icon name="rocket" variant="solid" size="xs" />}
                    onClick={onGoToLaunch}
                    disabled={launchDisabledReason !== null}
                    aria-describedby={
                      launchDisabledReason !== null ? 'versions-launch-reason' : undefined
                    }
                  >
                    Go to {launchLabel}
                  </Button>
                </>
              )}
            </div>
          </div>
          {/* Said on screen as well as to a screen reader: a disabled button is skipped by Tab. */}
          {showGoToLaunch && launchDisabledReason !== null && (
            <p className="draft-launch-reason" id="versions-launch-reason">
              {launchDisabledReason}
            </p>
          )}
          {staleDraft && (
            <Note tone="butter" className="draft-stale">
              Production has been launched again since this draft was started.
            </Note>
          )}
          {changesBlock}
          {canDiscard && (
            <Tooltip
              open={discardTip !== null}
              anchor={discardTip ?? { x: 0, y: 0 }}
              placement="bottom"
              title={DISCARD_HINT}
            />
          )}
        </DraftCard>

        {lockReason && (
          <Note tone="neutral" id="versions-lock-reason">
            {lockReason}
          </Note>
        )}

        {/* Always mounted, so a repeat failure is announced as a change of text (see above). */}
        <StatusLine role="status">
          {(read.state === 'failed' || historyRetry) && (
            <>
              {read.state === 'failed'
                ? "Couldn't load the launch history."
                : 'Loading the launch history…'}
              <Button
                type="button"
                variant="ghost"
                size="xs"
                aria-label="Try loading the launch history again"
                loading={historyRetry}
                onClick={() => {
                  setHistoryRetrying(true);
                  onRetryHistory();
                }}
              >
                Try again
              </Button>
            </>
          )}
        </StatusLine>
        {noLaunches && <p>No launches yet. The first launch becomes v1.</p>}
        {(read.state === 'loading' || rows.length > 0) && (
          <Table<HistoryRow>
            aria-label="Launches"
            surface="opaque"
            columns={columns}
            data={rows}
            keyExtractor={row => row.key}
            loading={read.state === 'loading'}
            loadingMessage="Loading the launch history…"
          />
        )}
        <Footnote>{HISTORY_FOOTNOTE}</Footnote>
        <Note tone="peri" icon={<Icon name="circle-info" variant="solid" size="xs" />}>
          Name, address, timezone, opening hours and the asset register belong to the twin, not a
          version. Loading or discarding a version leaves them as they are.
        </Note>
      </Body>
    </Modal>
  );
};

/** The draft's changes as one pill per step (R4), in a list named for a screen reader. */
export const ChangePills: React.FC<{ pills: ChangePill[]; className?: string }> = ({
  pills,
  className,
}) => (
  <PillList className={className} aria-label="Changes in your draft">
    {pills.map(pill => (
      <li key={pill.step}>
        <Tag surface="on-solid" tone="neutral">
          <span className="pill-step">{pill.label}</span>
          {pill.text}
        </Tag>
      </li>
    ))}
  </PillList>
);

export default VersionsModal;
